import { useEffect, useMemo, useRef } from 'react';
import { ECoinflipSide } from '@/utils/types/ICoinflipGame';
import { IUser } from '@/utils/types/User';
import { getAvatarVars, toFullSteamAvatar } from '@/utils/avatar';
import { getUserColor } from '@/utils/colors';
import { useSyncedFlip } from '@/utils/hooks/use-synced-flip';

type Phase = 'idle' | 'flipping' | 'won' | 'settled';

interface Props {
    phase: Phase;
    p1?: IUser;
    p2?: IUser;
    winnerSide?: ECoinflipSide;
    winnerNick: string;
    amount: number;
    flipStartAt?: number | null;
    revealAt?: number | null;
}

const TURNS = 13;
const COIN_SIZE = 220;

const formatMoney = (v: number) => `$${v.toFixed(2)}`;

const CoinflipBigCoin = ({
    phase,
    p1,
    p2,
    winnerSide,
    winnerNick,
    amount,
    flipStartAt,
    revealAt,
}: Props) => {
    const amountRef = useRef<HTMLDivElement>(null);
    const coinRef = useRef<HTMLDivElement>(null);

    const endDeg =
        TURNS * 360 +
        (winnerSide === ECoinflipSide.FIRST
            ? 0
            : winnerSide === ECoinflipSide.SECOND
              ? 180
              : 0);

    // RAF-driven, server-synced spin while flipping. Lands the coin on endDeg
    // exactly at revealAt — the same instant the parent flips phase → 'won'
    // (both keyed off revealAt), so the bounce starts with no snap.
    useSyncedFlip({
        ref: coinRef,
        endDeg,
        active: phase === 'flipping',
        flipStartAt,
        revealAt,
    });

    // Preload both avatar images so they're cached by the time the coin starts spinning
    useEffect(() => {
        if (typeof window === 'undefined') return;
        [p1?.avatar, p2?.avatar].forEach((url) => {
            if (!url) return;
            const img = new window.Image();
            img.src = url;
        });
    }, [p1?.avatar, p2?.avatar]);

    // Count-up suma — closure flag instead of useState (state caused effect to re-run and cancel itself)
    useEffect(() => {
        if (phase !== 'won') return;
        const el = amountRef.current;
        if (!el) return;

        el.textContent = formatMoney(0);

        let cancelled = false;
        let startTime: number | null = null;
        const dur = 1400;

        const step = (now: number) => {
            if (cancelled || !el) return;
            if (startTime === null) startTime = now;
            const t = Math.min(1, (now - startTime) / dur);
            const e = 1 - Math.pow(1 - t, 3);
            el.textContent = formatMoney(amount * e);
            if (t < 1) requestAnimationFrame(step);
            else el.textContent = formatMoney(amount); // ensure final exact value
        };

        // Start a bit after the banner appears (bannerIn has 0.5s delay)
        const handle = window.setTimeout(() => requestAnimationFrame(step), 600);

        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [phase, amount]);

    if (phase === 'idle') return null;

    // 'flipping' rotation is owned imperatively by useSyncedFlip (RAF). Only the
    // landing bounce stays a CSS keyframe. The bounce keyframes are all pinned
    // to rotateY(var(--coin-end-big)), so they pick up exactly where the RAF
    // left the coin (endDeg) — no jump.
    const flipAnim =
        phase === 'won'
            ? 'coinBounce 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) forwards'
            : 'none';

    return (
        <div
            className="absolute inset-0 flex flex-col items-center justify-center gap-5 pointer-events-none z-[2] transition-[opacity,transform] duration-500 ease-out"
            style={{
                opacity: phase === 'settled' ? 0 : 1,
                transform: phase === 'settled' ? 'scale(0.9)' : 'none',
            }}
        >
            <div
                className="w-[220px] h-[220px] max-md:w-[160px] max-md:h-[160px]"
                style={{ perspective: '1200px' }}
            >
                <div
                    ref={coinRef}
                    className="relative w-full h-full"
                    style={
                        {
                            transformStyle: 'preserve-3d',
                            ['--coin-end-big' as string]: `${endDeg}deg`,
                            animation: flipAnim,
                            // While flipping, RAF owns transform — leave it out of
                            // React's style so re-renders don't clobber the inline
                            // value. 'won' is driven by the bounce keyframe. Only
                            // 'settled' holds the final angle via React.
                            ...(phase === 'settled'
                                ? { transform: `rotateY(${endDeg}deg)` }
                                : {}),
                        } as React.CSSProperties
                    }
                >
                    <CoinFace user={p1} side={1} />
                    <CoinFace user={p2} side={2} />
                </div>
            </div>

            <div
                className="flex flex-col items-center gap-1.5 text-center"
                style={
                    phase === 'won'
                        ? {
                              opacity: 0,
                              transform: 'translateY(20px)',
                              animation:
                                  'bannerIn 0.55s cubic-bezier(0.34,1.56,0.64,1) 0.5s forwards',
                          }
                        : phase === 'settled'
                          ? { opacity: 1 }
                          : { opacity: 0 }
                }
            >
                <div
                    className="font-chat-nick text-[11px] font-extrabold tracking-[0.26em] uppercase"
                    style={{
                        background: 'linear-gradient(135deg, #b7a6ff, #5fe0ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        filter: 'drop-shadow(0 0 10px rgba(91,224,255,0.6))',
                    }}
                >
                    ★ Winner
                </div>
                <div
                    className="text-xl max-md:text-lg font-extrabold text-white max-w-[360px] truncate"
                    style={{ textShadow: '0 0 14px rgba(139,109,255,0.55)' }}
                >
                    {winnerNick}
                </div>
                <div
                    ref={amountRef}
                    className="font-chat-nick text-[42px] max-md:text-[28px] font-extrabold tabular-nums leading-none mt-1.5"
                    style={{
                        background: 'linear-gradient(135deg, #b7a6ff 0%, #5fe0ff 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        filter: 'drop-shadow(0 0 18px rgba(91,224,255,0.55))',
                        letterSpacing: '-0.02em',
                    }}
                >
                    {formatMoney(amount)}
                </div>
            </div>
        </div>
    );
};

function CoinFace({ user, side }: { user?: IUser; side: 1 | 2 }) {
    // Border matches the user's LEVEL colour (same as the row avatars),
    // not the global brand --p1/--p2 accent.
    const color = user
        ? getUserColor(user.experience ?? 0)
        : side === 1
          ? 'var(--p1)'
          : 'var(--p2)';
    const seed = user?.name || String(user?.id ?? '');
    const vars = useMemo(() => getAvatarVars(seed), [seed]);

    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '24%',
                overflow: 'hidden',
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                boxShadow: `0 0 0 4px ${color}, 0 0 60px -8px ${color}, inset 0 0 40px rgba(0,0,0,0.55)`,
                transform: side === 2 ? 'rotateY(180deg)' : undefined,
                background: '#14161c',
            }}
        >
            {user?.avatar ? (
                /* Plain <img> — bypasses next/Image optimizer so the avatar is loaded
                   directly from Steam CDN and is visible the moment the spin starts. */
                <img
                    src={toFullSteamAvatar(user.avatar)}
                    alt=""
                    width={COIN_SIZE}
                    height={COIN_SIZE}
                    draggable={false}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                    }}
                />
            ) : user ? (
                <div
                    className="abstract-avatar aa-square"
                    style={{ ...(vars as React.CSSProperties), width: '100%', height: '100%' }}
                    aria-hidden
                />
            ) : (
                <div className="w-full h-full grid place-items-center text-text_3 text-3xl font-light">
                    ?
                </div>
            )}
            {/* Soft inner highlight to keep the "coin" look */}
            <div
                aria-hidden
                style={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '24%',
                    pointerEvents: 'none',
                    background:
                        'radial-gradient(circle at 30% 22%, rgba(255,255,255,0.18), transparent 45%), radial-gradient(circle at 70% 80%, rgba(0,0,0,0.30), transparent 50%)',
                }}
            />
        </div>
    );
}

export default CoinflipBigCoin;
